import {
    useState,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useEffect,
    useMemo,
} from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'

// antd
import { message, Input, Button, Typography, Tooltip } from 'antd'
import { Icon } from '@iconify/react'

// slices
import {
    mainTaskCreate,
    mainTaskUpdate,
    taskCreateSupport,
    taskUpdateSupport,
} from '../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
    resolveReferenceId,
} from '../../taskRefactored/tasks.service'

// components
import DateRangePicker from './DateRangePicker'
import AssigneeSelectorButton from './AssigneeSelectorButton'
import { filterSupportQuickFilterSubTasks } from './supportQuickFilterSubtasks'

// css
import styles from './TaskSubTaskAccordion.module.css'

import unicornFxImage from '../../../assets/images/task/unicorn.png'

const { TextArea } = Input
const { Text } = Typography

const resolveSubTaskAssigneeId = (assigneeId) =>
    resolveReferenceId(assigneeId?.[0] ?? assigneeId)

const TaskSubTaskAccordion = forwardRef(function TaskSubTaskAccordion(
    {
        taskId,
        projectId,
        filteredAssignees = [],
        getAssigneeColor,
        getInitials,
        onTaskUpdate,
        onTaskClick,
        parentTask,
        taskListKind = 'support',
        readOnly = false,
        listQuery = null,
        openInTaskDetail = false,
    },
    ref
) {
    const dispatch = useDispatch()
    const isMainTask = taskListKind === 'main'
    const canAddSubtasks =
        !readOnly && !/^bug$/i.test(String(parentTask?.type || 'Task').trim())

    const [isExpanded, setIsExpanded] = useState(false)
    /**
     * The accordion body is only hidden with CSS (max-height), so every board
     * card used to mount a full row — date picker + assignee popover — for each
     * of its subtasks even while collapsed. Mount it on first expand instead,
     * and keep it mounted afterwards so the collapse transition still runs.
     */
    const [hasEverExpanded, setHasEverExpanded] = useState(false)
    const [createChild, setCreateChild] = useState(false)
    const [subTaskLoadingButton, setSubTaskLoadingButton] = useState(false)
    const [titleValue, setTitleValue] = useState('')
    const [hoveredSubTaskId, setHoveredSubTaskId] = useState(null)
    const [togglingSubTaskId, setTogglingSubTaskId] = useState(null)
    const [showCompletionCelebration, setShowCompletionCelebration] =
        useState(false)
    const subTaskInputRef = useRef(null)
    /** Enter and blur both submit the composer; blur fires again when the
     * textarea is disabled mid-request, so guard against a second create. */
    const creatingChildRef = useRef(false)

    /** Same as the “Add subtask” row button: expand accordion, toggle composer, focus when opening. */
    const openAddSubtaskComposer = useCallback(() => {
        if (!canAddSubtasks) return
        setIsExpanded(true)
        setCreateChild((prev) => {
            const next = !prev
            if (next) {
                setTimeout(() => {
                    const ta =
                        subTaskInputRef.current?.resizableTextArea?.textArea ||
                        subTaskInputRef.current
                    ta?.focus?.()
                }, 0)
            }
            return next
        })
    }, [canAddSubtasks])

    useImperativeHandle(ref, () => ({ openAddSubtaskComposer }), [
        openAddSubtaskComposer,
    ])

    useEffect(() => {
        if (!canAddSubtasks && createChild) {
            setCreateChild(false)
        }
    }, [canAddSubtasks, createChild])

    useEffect(() => {
        if (isExpanded) setHasEverExpanded(true)
    }, [isExpanded])

    const updateSubTask = useCallback(
        async (subTask, overrides = {}) => {
            if (!subTask?._id) {
                return { ok: false, message: 'Subtask not found' }
            }

            let result
            if (isMainTask) {
                const body = {
                    ...buildMainTaskUpdateBodyFromTask(
                        subTask,
                        projectId,
                        overrides
                    ),
                    ...(overrides.isCompleted !== undefined
                        ? { isCompleted: overrides.isCompleted }
                        : {}),
                }
                // Subtask updates must not resend parent linkage
                delete body.parent
                delete body.parentId
                result = await dispatch(
                    mainTaskUpdate({ id: subTask._id, body })
                )
            } else {
                const payload = {
                    _id: subTask._id,
                    updateTask: {
                        title: overrides.title ?? (subTask?.title || ''),
                        projectId,
                        type: overrides.type ?? (subTask?.type || 'Task'),
                        assigneeId:
                            overrides.assigneeId !== undefined
                                ? overrides.assigneeId
                                : resolveSubTaskAssigneeId(subTask?.assigneeId),
                        priority:
                            overrides.priority ?? (subTask?.priority || 'Low'),
                        status:
                            overrides.status ?? (subTask?.status || 'To Do'),
                        description:
                            overrides.description !== undefined
                                ? overrides.description
                                : subTask?.description || '',
                        start:
                            overrides.start !== undefined
                                ? overrides.start
                                : subTask?.start || null,
                        end:
                            overrides.end !== undefined
                                ? overrides.end
                                : subTask?.end || null,
                        ...(overrides.isCompleted !== undefined
                            ? { isCompleted: overrides.isCompleted }
                            : {}),
                    },
                }
                result = await dispatch(taskUpdateSupport(payload))
            }

            const data = result?.payload?.data
            return {
                ok: isMainTask
                    ? isTaskMutationSuccess(data)
                    : Boolean(data?.success),
                message: data?.message,
            }
        },
        [dispatch, isMainTask, projectId]
    )

    // Support quick filters only: hide subtasks that don't match Incomplete /
    // Completed / Just my tasks when those filters are active.
    const taskSubTasks = useMemo(() => {
        return filterSupportQuickFilterSubTasks(
            parentTask?.subTasks,
            listQuery,
            { applyFilter: !isMainTask }
        )
    }, [parentTask?.subTasks, isMainTask, listQuery])

    const createChildTask = async () => {
        if (!titleValue.trim() || creatingChildRef.current) {
            return
        }

        creatingChildRef.current = true
        setSubTaskLoadingButton(true)
        try {
            const parentEpic = resolveReferenceId(
                parentTask?.epic ?? parentTask?.epicId
            )
            const parentSprintId = resolveReferenceId(
                parentTask?.sprintId ?? parentTask?.sprint
            )
            const payload = {
                title: titleValue.trim(),
                parent: taskId,
                projectId,
                status: parentTask?.status || 'To Do',
                type: parentTask?.type || 'Task',
                ...(isMainTask && parentEpic ? { epic: parentEpic } : {}),
                ...(isMainTask && parentSprintId
                    ? { sprintId: parentSprintId }
                    : {}),
            }
            await dispatch(
                isMainTask
                    ? mainTaskCreate(payload)
                    : taskCreateSupport(payload)
            )
            setTitleValue('')
            setCreateChild(false)
            onTaskUpdate?.()
            message.success('Subtask created successfully')
        } catch (error) {
            message.error('Failed to create subtask')
        } finally {
            creatingChildRef.current = false
            setSubTaskLoadingButton(false)
        }
    }

    const handleDateRangeChange = async (subTask, dateRange) => {
        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                start: dateRange.start || null,
                end: dateRange.end || null,
            })
            if (ok) {
                onTaskUpdate?.()
            } else {
                message.error(
                    errorMessage || 'Failed to update subtask due date'
                )
            }
        } catch (error) {
            message.error('Failed to update subtask due date')
        }
    }

    const handleAssigneeChange = async (subTask, selected) => {
        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                assigneeId: selected?._id || null,
            })
            if (ok) {
                onTaskUpdate?.()
            } else {
                message.error(
                    errorMessage || 'Failed to update subtask assignee'
                )
            }
        } catch (error) {
            message.error('Failed to update subtask assignee')
        }
    }

    const isCompleted = (subTask) =>
        subTask?.isCompleted === true ||
        subTask?.status === 'completed' ||
        subTask?.status === 'done'

    const handleToggleSupportSubTaskComplete = async (subTask, e) => {
        e?.stopPropagation?.()
        if (readOnly || !subTask?._id || togglingSubTaskId === subTask?._id)
            return

        const nextCompleted = !isCompleted(subTask)
        setTogglingSubTaskId(subTask?._id)
        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                isCompleted: nextCompleted,
            })
            if (ok) {
                if (nextCompleted) setShowCompletionCelebration(true)
                onTaskUpdate?.()
            } else {
                message.error(
                    errorMessage || 'Failed to update subtask completion'
                )
            }
        } catch (error) {
            message.error('Failed to update subtask completion')
        } finally {
            setTogglingSubTaskId(null)
        }
    }

    const subtaskCount = taskSubTasks?.length

    return (
        <>
            {subtaskCount > 0 ? (
                <Tooltip
                    title={
                        openInTaskDetail
                            ? 'View subtasks'
                            : isExpanded
                              ? 'Hide subtasks'
                              : 'View subtasks'
                    }
                    placement="top"
                >
                    <div
                        className={styles.accordionHeader}
                        data-ignore-click
                        onClick={(e) => {
                            e.stopPropagation()
                            if (openInTaskDetail) {
                                onTaskClick?.(taskId, 'subtasks')
                                return
                            }
                            setIsExpanded(!isExpanded)
                        }}
                    >
                        <div className={styles.accordionHeaderContent}>
                            <span className={styles.subtaskCount}>
                                {subtaskCount}
                            </span>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Icon
                                    icon="mdi:source-branch"
                                    width={14}
                                    height={14}
                                    className={styles.branchIcon}
                                />
                                {!openInTaskDetail && (
                                    <Icon
                                        icon={
                                            isExpanded
                                                ? 'raphael:arrowdown'
                                                : 'raphael:arrowright'
                                        }
                                        width={12}
                                        height={12}
                                        className={styles.playIcon}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </Tooltip>
            ) : (
                <div className={styles.accordionHeader}>
                    <div className={styles.accordionHeaderContent} />
                </div>
            )}
            <div
                className={`${styles.accordionContent} ${
                    isExpanded ? styles.expanded : ''
                }`}
                data-ignore-click
                onClick={(e) => e.stopPropagation()}
            >
                {hasEverExpanded && (
                    <div className={styles.accordionContentWrapper}>
                        <div className={styles.subTasksList}>
                            {taskSubTasks?.map((subTask) => (
                                <div
                                    key={subTask?._id}
                                    className={styles.subTaskItem}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (onTaskClick && subTask?._id) {
                                            onTaskClick(subTask._id)
                                        }
                                    }}
                                >
                                    <div className={styles.subTaskContent}>
                                        {!isMainTask && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    border: `1px solid ${
                                                        isCompleted(subTask)
                                                            ? 'transparent'
                                                            : hoveredSubTaskId ===
                                                                subTask?._id
                                                              ? '#5da182'
                                                              : '#cbd5e1'
                                                    }`,
                                                    backgroundColor:
                                                        isCompleted(subTask)
                                                            ? '#5da182'
                                                            : hoveredSubTaskId ===
                                                                subTask?._id
                                                              ? '#f0fdf4'
                                                              : 'transparent',
                                                    transition:
                                                        'all 150ms ease, border-color 150ms ease, background-color 150ms ease',
                                                    flexShrink: 0,
                                                    cursor: readOnly
                                                        ? 'default'
                                                        : togglingSubTaskId ===
                                                            subTask?._id
                                                          ? 'not-allowed'
                                                          : 'pointer',
                                                    opacity:
                                                        togglingSubTaskId ===
                                                        subTask?._id
                                                            ? 0.6
                                                            : 1,
                                                }}
                                                onMouseEnter={
                                                    readOnly
                                                        ? undefined
                                                        : () =>
                                                              setHoveredSubTaskId(
                                                                  subTask?._id
                                                              )
                                                }
                                                onMouseLeave={
                                                    readOnly
                                                        ? undefined
                                                        : () =>
                                                              setHoveredSubTaskId(
                                                                  null
                                                              )
                                                }
                                                onClick={(e) =>
                                                    handleToggleSupportSubTaskComplete(
                                                        subTask,
                                                        e
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="iconamoon:check-light"
                                                    width={11}
                                                    height={11}
                                                    style={{
                                                        color: isCompleted(
                                                            subTask
                                                        )
                                                            ? '#ffffff'
                                                            : hoveredSubTaskId ===
                                                                subTask?._id
                                                              ? '#5da182'
                                                              : '#94a3b8',
                                                        transition:
                                                            'color 150ms ease',
                                                    }}
                                                />
                                            </span>
                                        )}
                                        <span
                                            className={styles.subTaskText}
                                            title={subTask?.title || 'Untitled'}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (
                                                    onTaskClick &&
                                                    subTask?._id
                                                ) {
                                                    onTaskClick(subTask._id)
                                                }
                                            }}
                                        >
                                            {subTask?.title || 'Untitled'}
                                        </span>
                                    </div>
                                    <div className={styles.subTaskActions}>
                                        <div
                                            className={styles.datePickerWrapper}
                                            onClick={(e) => e.stopPropagation()}
                                            style={
                                                readOnly
                                                    ? { pointerEvents: 'none' }
                                                    : undefined
                                            }
                                        >
                                            <DateRangePicker
                                                value={{
                                                    start: subTask?.start,
                                                    end: subTask?.end,
                                                }}
                                                onChange={
                                                    readOnly
                                                        ? undefined
                                                        : (dateRange) =>
                                                              handleDateRangeChange(
                                                                  subTask,
                                                                  dateRange
                                                              )
                                                }
                                                placeholder=""
                                            />
                                        </div>
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={
                                                readOnly
                                                    ? { pointerEvents: 'none' }
                                                    : undefined
                                            }
                                        >
                                            <AssigneeSelectorButton
                                                selectedAssignee={
                                                    subTask?.assigneeId
                                                }
                                                onSelect={
                                                    readOnly
                                                        ? undefined
                                                        : (selected) =>
                                                              handleAssigneeChange(
                                                                  subTask,
                                                                  selected
                                                              )
                                                }
                                                projectId={projectId}
                                                filteredAssignees={
                                                    filteredAssignees
                                                }
                                                getAssigneeColor={
                                                    getAssigneeColor
                                                }
                                                getInitials={getInitials}
                                                taskListKind={taskListKind}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {createChild && canAddSubtasks && (
                                <div
                                    className={styles.subTaskItem}
                                    style={{
                                        backgroundColor: '#f7f5ff',
                                    }}
                                >
                                    <div className={styles.subTaskContent}>
                                        <Icon
                                            icon="simple-line-icons:check"
                                            width={16}
                                            height={16}
                                            style={{ color: '#94a3b8' }}
                                        />
                                        <TextArea
                                            value={titleValue}
                                            onChange={(e) =>
                                                setTitleValue(e.target.value)
                                            }
                                            onBlur={createChildTask}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' &&
                                                    !e.shiftKey
                                                ) {
                                                    e.preventDefault()
                                                    createChildTask()
                                                }
                                            }}
                                            disabled={subTaskLoadingButton}
                                            ref={subTaskInputRef}
                                            autoFocus
                                            autoSize={{
                                                minRows: 1,
                                                maxRows: 4,
                                            }}
                                            className={styles.subTaskInput}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className={styles.subTaskActions}>
                                        <div
                                            className={styles.datePickerWrapper}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <DateRangePicker
                                                value={{
                                                    start: null,
                                                    end: null,
                                                }}
                                                onChange={() => {}}
                                                placeholder=""
                                            />
                                        </div>
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <AssigneeSelectorButton
                                                selectedAssignee={null}
                                                onSelect={() => {}}
                                                projectId={projectId}
                                                filteredAssignees={
                                                    filteredAssignees
                                                }
                                                getAssigneeColor={
                                                    getAssigneeColor
                                                }
                                                getInitials={getInitials}
                                                taskListKind={taskListKind}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={styles.subTasksActions}>
                            {canAddSubtasks ? (
                                <Button
                                    type="text"
                                    icon={
                                        <Icon
                                            icon="ic:baseline-plus"
                                            width={16}
                                            height={16}
                                        />
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        openAddSubtaskComposer()
                                    }}
                                    className={styles.addSubTaskButton}
                                >
                                    <Text className={styles.addSubTaskText}>
                                        Add subtask
                                    </Text>
                                </Button>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
            {showCompletionCelebration &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={styles.taskCompleteUnicornFx}
                        onAnimationEnd={() =>
                            setShowCompletionCelebration(false)
                        }
                    >
                        <img
                            className={styles.taskCompleteUnicornSvg}
                            src={unicornFxImage}
                            alt="Subtask completed celebration"
                            aria-hidden="true"
                        />
                    </div>,
                    document.body
                )}
        </>
    )
})

export default TaskSubTaskAccordion
